import '@ant-design/v5-patch-for-react-19';

import 'ALIASED-antd/dist/reset.css';
import 'react-phone-number-input/style.css';
import '@static/app.scss';
import '@static/calendar.css';
import '@static/slick.css';
import '@static/helper.css';
import 'react-image-crop/dist/ReactCrop.css';
import 'vl-common/src/components/BookAppointment/calendar.css';
import 'vl-common/src/components/TimeLine/provider-card.css';
import 'survey-core/survey-core.min.css';
import 'vl-common/src/components/CustomSurvey/customWidgets/ImageCrop.css';
import 'vl-common/assets/sass/app/app.scss';
import 'vl-common/assets/sass/app/add-patient.css';
import 'vl-md/src/assets/MdModal.css';

import { authInit, deriveAppRoute } from 'vl-common/src/init/init';
import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Auth } from 'aws-amplify';
import { Provider, useDispatch } from 'react-redux';
import Head from 'next/head';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import ReactGA from 'react-ga4';
import IdleTimer from 'react-idle-timer';
import initializeStore from '@store';
import useRuntimeConfig, { getUpdatedConfig, getDynamicConfig } from '@vl-core/hooks/useConfig';
import getConfig from 'next/config';
import type { AppProps } from '../stubs/next-types';
import useMounted from '@hooks/useMounted';
import LogRocket from 'logrocket';
import * as actionTypes from '@actionTypes';
import { ThemeContextWrapper } from '@lib/context/ThemeContextWrapper';
import { AppointmentContextWrapper } from 'vl-common/src/lib/context/Appointment';
import { Config, ConfigContext, getRuntimeConfig, setRuntimeConfig } from 'vl-common/src/hooks/Runtime';
import Footer from '@components/Layouts/Footer/Footer';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CookieContainer from '@components/CookieConsent/CookieContainer';
import DynamicStylesheet from '@components/DynamicStylesheet/DynamicStylesheet';
import checkWebSockets from '@components/PermissionsCheck/checkWebSockets';
import AppointmentWait from '@components/AppointmentWait/AppointmentWait';
import { CancelledAppointmentsProvider } from '@hooks/useCancelledAppointments';
import { useSafariPrompts } from '@hooks/useSafariPrompts';
import { useIsPWAEnabled } from '@hooks/useIsPWAEnabled';
import { initialise as initialise_i18n } from 'vl-common/src/lib/i18n';
import useIsRestricted from '@hooks/useIsRestricted';
import useAuth from '@vl-core/useAuth';
import useRedirectToBuildPath from 'vl-common/src/hooks/useRedirectToBuildPath';
import { HarReadyProvider, useHarReady } from 'vl-common/src/hooks/useHARReady';
import useHAR, { resetHARServiceWorkerAsRequired } from 'vl-common/src/hooks/useHAR';
import { showLogoutAlert } from 'vl-common/src/services/service.api';
import { getUser } from 'store/actions/userAccess';
import { userFetchResponse } from 'store/actions/userReducers';
import { MockWebSocketWithSubscriptions } from '@hooks/useSharedAppointmentWebSocket';
import { MockWebSocket } from '@hooks/useAppointmentEvents';
import { DismissibleToast } from '@components/DismissableToast';
import { usePleaseLogInAndRedirect } from '@hooks/usePleaseLogIn';
import { useDeepLink, UNAUTHENTICATED_ROUTES, getRelativePath } from '@hooks/useDeepLink';
import { ConfigProvider } from 'ALIASED-antd';
import { NotificationProvider } from '../contexts/NotificationContext/NotificationContext';
import { queryClient as _queryClient } from '@lib/queryClient';
import { PatientTestCallManager } from '@components/Video/PatientTestCallManager';
import { BookingProvider } from 'vl-common/src/lib/context/BookingContext/BookingContext';
import { getSessionUser } from 'vl-common/src/init/getSessionUser';

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((mod) => ({ default: mod.ReactQueryDevtools }))
);

const showReactQueryDevtools =
  import.meta.env.DEV && typeof window !== 'undefined' && window.location.hostname === 'localhost';

resetHARServiceWorkerAsRequired();

const { publicRuntimeConfig } = getConfig();

function cognitoInitialised() {
  const config = window.config;

  if (!config) throw Error('Expected config global to be already set up');

  Object.assign(publicRuntimeConfig, getUpdatedConfig(config));
  setRuntimeConfig(config);
  if (Auth.isInHARMode) {
    config.TOUR_PROVIDER = null;
  }

  if (typeof window !== 'undefined') {
    window.process ||= {
      // @ts-ignore
      env: { ...config }
    };
  }
}

export const defaultTheme = {
  cssVar: true,
  hashed: false,
  token: {
    colorLink: 'var(--ant-primary-color)',
    colorLinkHover: 'var(--ant-primary-color)',
    colorLinkActive: 'var(--ant-primary-color)',
    colorSuccess: '#09a846',
    colorWarning: '#faad14',
    colorError: '#ff0000',
    fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
    fontSize: 14,
    lineHeight: 1.77,
    borderRadius: 4,
    colorBorder: '#d9d9d9',
    controlOutline: '#3b99fc',
    controlOutlineWidth: 4
  },
  components: {
    Anchor: {
      colorLink: 'var(--ant-primary-color)',
      colorLinkHover: 'var(--ant-primary-color)',
      colorLinkActive: 'var(--ant-primary-color)'
    },
    Button: {
      borderRadius: 0,
      colorPrimary: 'var(--ant-primary-color)',
      colorPrimaryHover: 'var(--ant-primary-color-hover)',
      colorPrimaryActive: 'var(--ant-primary-color)',
      colorLink: 'var(--ant-primary-color)',
      colorLinkHover: 'var(--ant-primary-color)',
      colorLinkActive: 'var(--ant-primary-color)',
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      fontSize: 14
    },
    Radio: {
      colorPrimary: 'var(--ant-primary-color)',
      colorPrimaryHover: 'var(--ant-primary-color)',
      colorPrimaryActive: 'var(--ant-primary-color)'
    },
    Tabs: {
      colorPrimary: 'var(--ant-primary-color)',
      colorPrimaryHover: 'var(--ant-primary-color)',
      colorPrimaryActive: 'var(--ant-primary-color)'
    },
    Form: {
      colorTextDescription: '#636466'
    },
    Select: {
      colorBgContainer: '#ffffff',
      colorBorder: '#e5e5e5',
      colorPrimary: 'var(--ant-primary-color)',
      colorPrimaryHover: 'var(--ant-primary-color)'
    },
    Input: {
      borderRadius: 2,
      fontSize: 14,
      hoverBorderColor: 'var(--ant-primary-color)'
    },
    List: {
      itemPadding: '0px'
    },
    Steps: {
      colorPrimary: 'var(--ant-primary-color)'
    },
    Switch: {
      colorPrimary: 'var(--ant-primary-color)',
      colorPrimaryHover: 'var(--ant-primary-color)'
    },
    Checkbox: {
      colorPrimary: 'var(--ant-primary-color)',
      colorPrimaryHover: 'var(--ant-primary-color)'
    },
    Modal: {
      borderRadiusLG: 0,
      colorPrimary: 'var(--ant-primary-color)'
    }
  }
};

// keep the access token updated
const checkSession = async (pushToLogin: () => void) => {
  if (Auth.isInHARMode || window.location.href.includes('har=')) return true;

  try {
    await Auth.currentAuthenticatedUser();
  } catch (error) {
    const path = getRelativePath(window.location.pathname);
    if (!UNAUTHENTICATED_ROUTES.find((url) => path.startsWith(url))) {
      console.debug(`${new Date().toString()} no session, "${path}"`);
      console.error(error);
      if (!path.startsWith('/confirm-email')) {
        pushToLogin?.();
      }

      return false;
    }
  }

  return true;
};

const debouncedCheckSession = debounce(checkSession, 1000);

const guestAccessAllowedRoutes = [
  '/login/',
  '/axalogin/',
  '/linklogin/',
  '/wellbeinghub/',
  // JAE this an authenticate path.
  // '/create-account',

  '/forgot-password/',
  '/policy/',
  '/patients/',
  '/patients/face2face/',
  '/patients/session/',
  '/patients/assessments/',
  '/patients/choose/',
  '/patients/register/',
  '/patients/legal/',
  '/patients/support/',
  '/survey/',
  '/floating-video/',
  '/regulatory/',
  '/events-test/',
  '/accessibility/'
];

const WrappedHBSApp = ({
  Component,
  queryClient,
  componentProps
}: {
  Component: AppProps['Component'];
  queryClient: QueryClient;
  componentProps: AppProps['pageProps'];
}) => {
  useRedirectToBuildPath();
  const dispatch = useDispatch();
  const router = useRouter();
  const match = router.asPath.match(/[^?]+/);
  // remove trailing slash
  const pathname = match ? match[0] : router.asPath;
  const { user } = useAuth();
  const userGuid = useRef(user?.user_guid);
  const { LOG_ROCKET_APP_ID, LOG_ROCKET_INPUT_SANITIZER = false, TWILIO_BACKEND_URL } = useRuntimeConfig();
  const isRestricted = useIsRestricted(false);

  const { pushToLogin } = useDeepLink();

  const [, setPleaseLogIn] = usePleaseLogInAndRedirect();

  useEffect(() => {
    if (
      user?.user_type_code === 'PATIENT' &&
      isRestricted &&
      !guestAccessAllowedRoutes.includes(`${pathname}/`.replaceAll('//', '/'))
    ) {
      setPleaseLogIn({
        pleaseLogIn: true,
        pleaseLogInRedirect: '/patients'
      });
    }
  }, [pathname, isRestricted, user, setPleaseLogIn]);

  useSafariPrompts();

  useEffect(() => {
    if (LOG_ROCKET_APP_ID && user) {
      LogRocket.init(LOG_ROCKET_APP_ID, { dom: { inputSanitizer: LOG_ROCKET_INPUT_SANITIZER } });

      const { user_type_code, first_name, last_name, tel_mobile_no, email, user_guid } = user;
      const traits = {
        email,
        name: `${first_name} ${last_name}`,
        id: user_guid,
        user_type_code,
        tel_mobile_no
      };

      LogRocket.identify(email, traits);

      dispatch({ type: actionTypes.LOG_ROCKET_INITIALISED });
    }
  }, [LOG_ROCKET_APP_ID, LOG_ROCKET_INPUT_SANITIZER, user, dispatch]);

  // Whenever the user_guid changes, reset the cache so there's no danger that cached data
  // bleeds from one user to another.
  // NB - normally I'd use useEffect() for this sort of thing but that runs *after* rendering
  // which means that some child components may have already initiated react-query requests.
  if (userGuid.current !== user?.user_guid) {
    console.log('Resetting react-query cache on user change');

    if (queryClient) {
      queryClient
        .getQueriesData<WebSocket>({ queryKey: ['shared-websocket'] })
        .filter(([, qd]) => qd)
        .forEach(([, qd]) => {
          console.log('Closing shared-websocket', qd);
          qd?.close();
        });
      queryClient
        .getQueriesData<WebSocket>({ queryKey: ['useAppointmentEvents'] })
        .filter(([, qd]) => qd)
        .forEach(([, qd]) => {
          console.log('Closing useAppointmentEvents web socket', qd);
          qd?.close();
        });

      queryClient.clear();
    }
    userGuid.current = user?.user_guid;
  }

  useEffect(() => {
    if (router) {
      debouncedCheckSession(pushToLogin);

      // 150 seconds === half the Cognito token expiry
      const timer = setInterval(() => checkSession(pushToLogin), 150 * 1000);

      return () => clearInterval(timer);
    }

    return () => {};
  }, [router, dispatch]);

  if (user?.user_guid) checkWebSockets(TWILIO_BACKEND_URL);

  return (
    <div className="main-content-area">
      <Suspense fallback={<p>Initialising...</p>}>
        <Component {...componentProps} />
      </Suspense>
    </div>
  );
};

function useStoreAndMaybeRedirect() {
  const store = useMemo(() => initializeStore(), []);

  useEffect(() => {
    let active = true;

    if (store) {
      getSessionUser().then((user) => {
        if (active) {
          // saves u in Redux
          store.dispatch(userFetchResponse(actionTypes.AUTH_SUCCESS, user, '', false));
        }
      });
    }

    return () => {
      active = false;
    };
  }, [store]);

  return { store, storeReady: true };
}

// Loads up the user record into Redux if in HAR mode. Assumes HAR is all hooked up.
const MaybeLoadUser = () => {
  const { user } = useAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchUser = async () => {
      const { data: vlUser } = await getUser(Auth.harUserId, '', dispatch);

      await dispatch(userFetchResponse(actionTypes.AUTH_SUCCESS, vlUser, '', false));
    };

    if (!user && Auth.isInHARMode) {
      fetchUser().then();
    }

    // disable WebSockets
    MockWebSocket.MOCKED_WEB_SOCKETS = true;
    MockWebSocketWithSubscriptions.MOCKED_WEB_SOCKETS = true;
  }, [dispatch, user]);

  return null;
};

const HTMLHead = ({ stonlyTourActive }: { stonlyTourActive: boolean }) => {
  const isPWAEnabled = useIsPWAEnabled();
  const { user } = useAuth();
  const { LIVE_CHAT: liveChatEnabled } = useRuntimeConfig();
  const { t, ready } = useTranslation('common');
  const isClinician = typeof window !== 'undefined' && window.location.hostname.includes('clin');
  const defaultTitle = `${t('AppTitleShort')} - ${isClinician ? 'Clinician' : 'Patient'}`;

  return (
    <Head key={Number(ready)}>
      <title>{defaultTitle}</title>
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0" />
      {stonlyTourActive && <script async src="/scripts/stonly.js" />}
      {isPWAEnabled && <link rel="manifest" href="/static/site.webmanifest" />}
      {liveChatEnabled && user && user?.user_type_code === 'PATIENT' && (
        <>
          <script
            src="https://eu-west-1-prod-webchat.cxengage.net/load-chat.js"
            data-cxengage-web-integration-id="6788efa94d3151cbe48894d1"
            data-cxengage-tenant-id="919091b5-b41d-44f0-aa7a-2f49bda89de4"
            data-cxengage-region="eu-1"
          />
          <script src="https://us-east-1-prod-webchat.cxengage.net/init-chat.js" />
        </>
      )}
    </Head>
  );
};

const HBSApp = ({ Component, pageProps }: AppProps) => {
  const idleTimer = useRef<IdleTimer | null>(null);
  const [queryClient] = useState(_queryClient);
  const mounted = useMounted();
  const [contextValue, setContextValue] = useState<Config>();
  const [amplifyConfigured, setAmplifyConfigured] = useState(false);
  const { store, storeReady } = useStoreAndMaybeRedirect();
  const [googleAnalyticsId, setGoogleAnalyticsId] = useState('');
  const [performanceCookiesAccepted, setPerformanceCookiesAccepted] = useState(false);
  const BASEPATH = process.env.BASEPATH || '';
  const [i18nReady, setI18nReady] = useState(false);
  const { isReady } = useHarReady();

  const { pushToLogin } = useDeepLink();

  const urlParams =
    typeof window !== 'undefined' ? Object.fromEntries(new URLSearchParams(window?.location?.search).entries()) : {};
  const { error_description, source_url } = urlParams;

  useHAR(getDynamicConfig);

  useEffect(() => {
    initialise_i18n(BASEPATH).then(() => setI18nReady(true));
  }, [BASEPATH]);

  useEffect(() => {
    // since configureAmplify potentially overwrites localStorage(!), wait until Amplify is configured before
    // trying to set the performance cookies state variable.
    if (amplifyConfigured && localStorage) {
      setPerformanceCookiesAccepted(
        localStorage.getItem('cookie-consent') === 'all' || localStorage.getItem('cookie-consent') === 'per'
      );
    }
  }, [amplifyConfigured]);

  const stonlyTourActive =
    !Auth.isInHARMode && publicRuntimeConfig.TOUR_PROVIDER === 'STONLY' && performanceCookiesAccepted;

  const checkForHighContrast = () => {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;

    if (sessionStorage.getItem('contrast') === 'high') document.body.setAttribute('data-contrast', 'high');
    if (sessionStorage.getItem('contrast') === 'normal') document.body.setAttribute('data-contrast', 'normal');
  };

  checkForHighContrast();

  useEffect(() => {
    cognitoInitialised();

    const { GOOGLE_ANALYTICS_TAG, IDENTITY_PROVIDERS } = getRuntimeConfig();

    // this if statement is for the AXA SSO forgot password redirect
    // has to happen before later variables which might redirect to another page
    // and prevent the forgot password flow from working
    if (error_description && IDENTITY_PROVIDERS?.providers) {
      const errorCode = error_description.split(':')[0];
      const fallbackProviderName = `-ERR-${errorCode}`;
      const handlerProvider = IDENTITY_PROVIDERS.providers?.find((p) => p.provider?.includes(fallbackProviderName));
      if (handlerProvider?.provider) {
        Auth.federatedSignIn({ provider: handlerProvider.provider }).then();
        return;
      }
      // Set the sessionStorage so that other errors can be handled in login.tsx
      sessionStorage.setItem('error_description', error_description);

      // Page does not load if the following is not run
      const { search, origin, pathname } = window.location;
      const searchParams = new URLSearchParams(search);

      searchParams.delete('error_description');
      window.history.pushState({}, '', `${origin}${pathname}?${searchParams.toString()}`);
    }
    setAmplifyConfigured(true);
    setContextValue(getRuntimeConfig());
    if (!Auth.isInHARMode) {
      // @ts-ignore the Config property names have not been uppercased yet to match the TypeScript type declaration
      setGoogleAnalyticsId(GOOGLE_ANALYTICS_TAG);
    }
    // const client_translations = await fetch(`${BASEPATH}/locales/${config.theme.toLowerCase()}/en/common.json`);
    // if (client_translations.ok) {
    //   i18n.addResources('en', 'common', await client_translations.json());
    // }
  }, [BASEPATH, error_description]);

  useEffect(() => {
    if (
      googleAnalyticsId &&
      sessionStorage.getItem('ga-configured') !== 'true' &&
      performanceCookiesAccepted &&
      !Auth.isInHARMode
    ) {
      const options: { campaign_source?: string; campaign_id?: string } = {};
      if (source_url) options.campaign_source = source_url;
      if (sessionStorage.getItem('campaign_id')) options.campaign_id = sessionStorage.getItem('campaign_id') || '';

      ReactGA.initialize(googleAnalyticsId, {
        gaOptions: options
      });
      sessionStorage.setItem('ga-configured', 'true');
    }
  }, [googleAnalyticsId, performanceCookiesAccepted]);

  const onIdle = async () => {
    if (Auth.isInHARMode) return;
    if (idleTimer.current?.isIdle()) {
      await Auth.signOut().catch();
      await pushToLogin();
      showLogoutAlert(`You were logged out due to session timeout (currently on ${window.location.href})`);
    } else {
      idleTimer.current?.reset();
    }
  };

  const cookiePreferencesUpdated = () => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const cookiePrefs = localStorage.getItem('cookie-consent');
      setPerformanceCookiesAccepted(cookiePrefs ? cookiePrefs === 'all' || cookiePrefs.includes('per') : false);
    }
  };

  if (!mounted() || !i18nReady || error_description || !contextValue) return null;

  return (
    <ConfigProvider theme={defaultTheme} virtual={false}>
      <NotificationProvider>
        <CancelledAppointmentsProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeContextWrapper>
              <Provider store={store}>
                <DynamicStylesheet />
                <ConfigContext.Provider value={contextValue}>
                  <HTMLHead stonlyTourActive={stonlyTourActive} />
                  <IdleTimer
                    ref={(ref: IdleTimer) => {
                      idleTimer.current = ref;
                    }}
                    onIdle={onIdle}
                    startOnMount
                    debounce={250}
                    timeout={1000 * 60 * 60}
                  />
                  {!storeReady && <p style={{ color: 'lightgray' }}>Initialising Application</p>}
                  <div>
                    <DismissibleToast />
                    {storeReady && contextValue && (
                      <>
                        <PatientTestCallManager>
                          <BookingProvider>
                            <AppointmentContextWrapper>
                              <div className="app-viewport">
                                {isReady && (
                                  <WrappedHBSApp
                                    Component={Component}
                                    queryClient={queryClient}
                                    componentProps={pageProps}
                                  />
                                )}
                                <WrappedFooter />
                              </div>
                            </AppointmentContextWrapper>
                          </BookingProvider>
                        </PatientTestCallManager>
                        {!Auth.isInHARMode && (
                          <CookieContainer
                            cookiePreferencesUpdated={cookiePreferencesUpdated}
                            tourActive={stonlyTourActive}
                          />
                        )}
                        {Auth.isInHARMode && isReady && <MaybeLoadUser />}
                      </>
                    )}
                  </div>
                </ConfigContext.Provider>
              </Provider>
            </ThemeContextWrapper>
            <div data-chromatic="ignore">
              {showReactQueryDevtools && (
                <Suspense>
                  <ReactQueryDevtools initialIsOpen={false} />
                </Suspense>
              )}
            </div>
          </QueryClientProvider>
        </CancelledAppointmentsProvider>
      </NotificationProvider>
    </ConfigProvider>
  );
};

const WrappedFooter = () => {
  const { user } = useAuth();
  return (
    <>
      <Footer />
      {user?.self_reg_required === false && !!user?.policies_accepted && <AppointmentWait />}
    </>
  );
};

const initialising = typeof window === 'undefined' ? Promise.reject() : authInit();

const Top = (props: any) => {
  const [readyToRender, setReadyToRender] = useState(false);
  const router = useRouter();
  const runCount = useRef(0);

  useEffect(() => {
    if (runCount.current > 0) return;

    // only execute once, even in StrictMode, so I don't repeat the database fetches inside deriveAppRoute()
    runCount.current++;
    initialising.then(async (redirected) => {
      const session = await Auth.fetchAuthSession();
      const alreadySignedIn = !!session.tokens;

      if (router.pathname === '/' && alreadySignedIn) {
        const sessionUser = await getSessionUser();
        const derivedAppRoute = await deriveAppRoute(sessionUser);
        // I'm returning from an OAuth redirectSignIn, redirect to an appropriate initial page *before* the first render
        await router.replace(derivedAppRoute);
      }

      setReadyToRender(!redirected);
    });
  }, []);

  if (!readyToRender || typeof window === 'undefined') return null;

  return (
    <HarReadyProvider>
      <HBSApp {...props} />
    </HarReadyProvider>
  );
};

export default Top;
